// @vendors
const Immutable = require('immutable');
const momentTz = require('moment-timezone');
const get = require('lodash/object/get');
const trim = require('lodash/string/trim');
const reduce = require('lodash/collection/reduce');

// @helpers
const apiURLBuilder = require('core/apiURLBuilder');

// @actions
const signaturePatternActions = require('actions/signaturePattern');
const errorActions = require('actions/brokerError');

// @utilities
const { APIGet, APIPost } = require('utilities/APIRequestHelper');
const DateHelper = require('utilities/dateHelper');
const PersonalDataHelper = require('utilities/personalDataHelper');
const { getConfirmationPDFSharesAmount } = require('utilities/pdfDocumentsHelper');
const { getCCOfficeChannel } = require('utilities/contactCenter/printerHelper');

// @constants
const actionTypes = require('constants/actionTypes');
const { es: { DATE_FORMAT_WITH_DASHES_DATETIME }} = require('commonsConstants/dateFormatTypes');
const {
    FIRST_STEP,
    SECOND_STEP,
    WIZARD_FIRST_STEP,
    WIZARD_SECOND_STEP,
    DATE_FORMAT_API,
    BROKER_ORDER_TYPE_TO_MARKET,
    BROKER_ORDER_TYPE_LIMITED,
    BROKER_ORDER_TYPE_FOR_THE_BEST,
    EURO_TEXT,
    BROKER_RIGHTS_BUY_CORPORATE_OPERATION,
    FOR_THE_BEST_CODE,
    LIMITED_CODE,
    TO_MARKET_CODE,
    CHARGE_ACCOUNT_PREFIX,
    CONNECTION_CHANNEL_INTERNET,
    BROKER_ORDER_TYPE_BUY,
    BROKER_CORPORATE_OPERATIONS_RIGHTS_PRODUCT_TYPE,
    BROKER_MIFID_VERSION_2,
    BROKER_ALERT_BUY_BUTTON_STOCK,
    BROKER_CORPORATE_OPERATIONS_BUY_RIGHTS_URL,
    BROKER_TYPE_RIGHT_NEW_DECIMALS,
    BROKER_ORDER_TYPE_FOR_THE_BEST_DESCRIPTION,
    BROKER_ORDER_TYPE_LIMITED_DESCRIPTION,
    BROKER_ORDER_TYPE_TO_MARKET_DESCRIPTION,
    BROKER_MIFID_RIGHTS_PDF_AMOUNT_DECIMALS,
    API_CHANNEL
} = require('constants/index');

const RIGHTS_BUY_AGENT_ID = 'WEB';

function selectedCorporateOperationIndexChanged(value) {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_SELECTED_CORPORATE_INDEX_CHANGED,
        payload: {
            value
        }
    };
}

function rightsQuantityChanged(value) {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_RIGHTS_QUANTITY_CHANGED,
        payload: {
            value
        }
    };
}

function validityDateChanged(value) {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_VALIDITY_DATE_CHANGED,
        payload: {
            value
        }
    };
}

function orderTypeChanged(value) {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_ORDER_TYPE_CHANGED,
        payload: {
            value
        }
    };
}

function limitedValueChanged(value) {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_LIMITED_VALUE_CHANGED,
        payload: {
            value
        }
    }
}

function reset() {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_RESET
    };
}

const setVisibleStep = step => ({
    type: actionTypes.BROKER_RIGHTS_BUY_SET_VISIBLE_STEP,
    payload: {
        step
    }
});

const validateUIStep = (step, catalogData) => ({
    type: actionTypes.BROKER_RIGHTS_BUY_VALIDATE_UI_STEP,
    payload: {
        step,
        catalogData
    }
});

function confirmRightsBuyRequest() {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_CONFIRM_REQUEST
    };
}

function confirmRightsBuySuccess(response) {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_CONFIRM_SUCCESS,
        payload: {
            orderNumber: response.numeroOrden
        }
    };
}

function confirmRightsBuyFailure(response) {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_CONFIRM_FAILURE,
        payload: {
            data: response
        }
    };
}

const getOrderTypeCode = (orderType) => {
    let result;

    switch (orderType) {
        case BROKER_ORDER_TYPE_TO_MARKET:
            result = TO_MARKET_CODE;
            break;
        case BROKER_ORDER_TYPE_LIMITED:
            result = LIMITED_CODE;
            break;
        case BROKER_ORDER_TYPE_FOR_THE_BEST:
            result = FOR_THE_BEST_CODE;
            break;
    }

    return result;
};

const mifidDisclaimersResponse = (immBrokerConvenienceMiFIDTest) => {
    const isUsingMifidv2 = immBrokerConvenienceMiFIDTest.get('mifidVersion') === BROKER_MIFID_VERSION_2;
    let disclaimers = undefined;

    if (isUsingMifidv2) {
        const mifidv2AllDisclaimers =
            immBrokerConvenienceMiFIDTest.getIn(['mifidv2', 'disclaimers'])
            .merge(immBrokerConvenienceMiFIDTest.getIn(['mifidv2', 'usPersonDisclaimers']))
            .toJS();

        disclaimers = reduce(mifidv2AllDisclaimers, (result, disclaimer, index) => {
            const disclaimerKey = `disclaimer_${index + 1}`;
            const disclaimerText = trim(get(disclaimer, 'description'));

            result[disclaimerKey] = `${trim(get(disclaimer, 'id'))} | ${disclaimerText}`;

            return result;
        }, {});
    }

    return disclaimers;
};

const removeRightsPrefixFromProductManager = (productManager) => {
    const rightsPrefix = /^derechos?/i;
    const productManagerWithoutRightsPrefix = trim(productManager).replace(rightsPrefix, '');
    return trim(productManagerWithoutRightsPrefix);
};

const getConfirmationRequestObject = (dataToken, getState) => {
    const immBrokerRightsBuy = getState().brokerRightsBuy;
    const immCorporatesOperations = getState().brokerCorporatesOperations;
    const immBrokerProductFile = getState().brokerProductFile;
    const immBrokerLegalDocumentation = getState().brokerLegalDocumentation;
    const uuid = immBrokerLegalDocumentation.get('uuid');

    const selectedCorporateOperationIndex = immBrokerRightsBuy.get(
        'selectedCorporateOperationIndex'
    );
    const selectedCorporateOperation = immCorporatesOperations.getIn([
        'selectedContract',
        'operations',
        selectedCorporateOperationIndex
    ]);
    const selectedContract = immCorporatesOperations.get('selectedContract');
    const validityDate = immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'validityDate']);
    const orderTypeOption = immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'orderTypeOption']);
    const rightsQuantity = immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'rightsQuantity']);

    const shares = Number(rightsQuantity) || 0;
    const lastProductPrice = immBrokerProductFile.getIn(['productValue', 'amount']);
    const productManager = removeRightsPrefixFromProductManager(immBrokerProductFile.get('companyName'));

    const immBrokerConvenienceMiFIDTest = getState().brokerConvenienceMiFIDTest;
    const mifidScore = immBrokerConvenienceMiFIDTest.getIn(['mifidv2', 'score']);
    const mifidScoring = immBrokerConvenienceMiFIDTest.getIn(['mifidv2', 'scoring']);
    const mifidWasApplied = immBrokerConvenienceMiFIDTest.get('started');

    const requestObject = {
        header: {
            version: 2
        },
        query: {
            agentId: RIGHTS_BUY_AGENT_ID,
            initQuantity: shares,
            accionesSuscrita: 0, // @hardcoded To confirm
            productManager,
            shares,
            unitPrice: Number(lastProductPrice) || 0,
            productType: BROKER_ALERT_BUY_BUTTON_STOCK,
            productIsin: trim(selectedCorporateOperation.get('isin')),
            micCode: trim(selectedCorporateOperation.get('iso')),
            codigoEmision: selectedCorporateOperation.get('codigoEmision'),
            codigoEntidadFinanciera: immBrokerRightsBuy.getIn([
                'validationServiceResponse',
                'codigoEntidadFinanciera'
            ]),
            codigoValor: selectedCorporateOperation.get('codigoValorInterno'),
            contrato: {
                numerodecontrato: selectedContract.getIn(['contract', 'number']),
                producto: selectedContract.getIn(['contract', 'product'])
            },
            dataToken,
            digitoDeControl: selectedContract.getIn(['cuenta', 'digitodecontrol']),
            fechaApertura: immBrokerRightsBuy.getIn(['validationServiceResponse', 'fechaApertura']),
            fechaSesion: DateHelper.formatDate(new Date(), DATE_FORMAT_API),
            formaPago: immBrokerRightsBuy.getIn(['validationServiceResponse', 'formaPago']),
            horaNegociacion: immBrokerRightsBuy.getIn([
                'validationServiceResponse',
                'horaNegociacion'
            ]),
            numDerechosValMin: 0,
            numeroDerechos: parseInt(
                immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'rightsQuantity'])
            ),
            plazoValidez: DateHelper.formatDate(validityDate, DATE_FORMAT_API),
            proporcionTitulosNuevos: immBrokerRightsBuy.getIn([
                'validationServiceResponse',
                'proporcionTitulosNuevos'
            ]),
            proporcionTitulosViejos: immBrokerRightsBuy.getIn([
                'validationServiceResponse',
                'proporcionTitulosViejos'
            ]),
            tipoEjecucion: immBrokerRightsBuy.getIn(['validationServiceResponse', 'tipoEjecucion']),
            tipoOperacion: BROKER_RIGHTS_BUY_CORPORATE_OPERATION,
            tipoOrden: getOrderTypeCode(orderTypeOption),
            convenientRegistry: immBrokerConvenienceMiFIDTest.getIn(['mifidv2', 'isConvenient']),
            evaluateRegistry: mifidWasApplied,
            disclaimers: mifidDisclaimersResponse(immBrokerConvenienceMiFIDTest),
            marketTargetRegistry: mifidScore >= mifidScoring,
            necessaryConvenienceTestNote: mifidScoring,
            necessaryTargetMarketNote: immBrokerConvenienceMiFIDTest.getIn(['mifidv2', 'targetMarketKnowledgeAndExperience']),
            testConveniencyResult: mifidScore,
            documentationId: uuid
        }
    };

    if (orderTypeOption === BROKER_ORDER_TYPE_LIMITED) {
        requestObject.query.importeLimitado = {
            divisa: EURO_TEXT,
            numeroimportevalores: immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'orderTypeValue']).toFixed(BROKER_TYPE_RIGHT_NEW_DECIMALS)
        };
    }

    return requestObject;
};

const generateMiFIDDisclaimers = (immBrokerConvenienceMiFIDTest) => {
    const immUSPersonDisclaimers = immBrokerConvenienceMiFIDTest.getIn(['mifidv2', 'usPersonDisclaimers']);
    const immDisclaimers = immBrokerConvenienceMiFIDTest.getIn(['mifidv2', 'disclaimers']);
    const immAllDisclaimers = Immutable.fromJS([]).merge(immUSPersonDisclaimers).merge(immDisclaimers);

    return immAllDisclaimers.map(immDisclaimer => immDisclaimer.get('description')).join(' | ');
};

const getOrderConditionDescription = orderTypeOption => {
    let description = '';

    switch(orderTypeOption) {
        case BROKER_ORDER_TYPE_FOR_THE_BEST:
            description = BROKER_ORDER_TYPE_FOR_THE_BEST_DESCRIPTION;
            break;
        case BROKER_ORDER_TYPE_LIMITED:
            description = BROKER_ORDER_TYPE_LIMITED_DESCRIPTION;
            break;
        case BROKER_ORDER_TYPE_TO_MARKET:
            description = BROKER_ORDER_TYPE_TO_MARKET_DESCRIPTION;
            break;
        default:
            break;
    }

    return description;
}

const storeDocument = (buyRightsResponse, history) => (dispatch, getState) => {
    const immProfile = getState().profile;
    const immCorporatesOperations = getState().brokerCorporatesOperations;
    const immBrokerRightsBuy = getState().brokerRightsBuy;
    const immBrokerProductFile = getState().brokerProductFile;
    const immFeatureFlags = getState().flagBasedFeatures;
    const showExantCostETFBuy = immFeatureFlags.get('wealth_etfs_buy_show_costs')
    const selectedCorporateOperationIndex = immBrokerRightsBuy.get('selectedCorporateOperationIndex');
    const immContract = immCorporatesOperations.get('selectedContract');
    const immOperation = immCorporatesOperations.getIn([
        'selectedContract',
        'operations',
        selectedCorporateOperationIndex
    ]);

    const orderId = trim(get(buyRightsResponse, 'numeroOrden'));
    const orderTimestamp =
        get(buyRightsResponse, 'timestamp') ||
        get(buyRightsResponse, 'timeResponse.timestamp');

    const momentEmittedDateTime = momentTz(Number(orderTimestamp)).tz("Europe/Madrid");

    const controlDigit = trim(immContract.getIn(['cuenta', 'digitodecontrol']));
    const productNumber = trim(immContract.getIn(['contract', 'product']));
    const contractNumber = trim(immContract.getIn(['contract', 'number']));
    const idContract = `${CHARGE_ACCOUNT_PREFIX}${controlDigit}${productNumber}${contractNumber}`;

    const owner = trim(immContract.get('owner'));
    const validityDate = immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'validityDate']);
    const orderTypeOption = immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'orderTypeOption']);

    const rightsQuantity = immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'rightsQuantity']);
    const sharesNumber = Number(rightsQuantity) || 0;
    const orderTypeValue = immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'orderTypeValue']);
    const isLimitedOrder = orderTypeOption === BROKER_ORDER_TYPE_LIMITED;

    const marketName = (__CONTACT_CENTER__ && trim(immBrokerProductFile.get('manager', ''))) || trim(immBrokerProductFile.get('marketName', ''));
    const productAmount = Number(immBrokerProductFile.getIn(['productValue','amount'])) || 0;
    const currency = (immBrokerProductFile.getIn(['productValue','currency']))
    const sharesAmount = getConfirmationPDFSharesAmount({
        isLimitedOrder,
        sharesNumber,
        lastPrice: productAmount,
        decimals: BROKER_MIFID_RIGHTS_PDF_AMOUNT_DECIMALS
    });
    const dataExtant = getState().brokerRightsBuy.getIn(['exAnteCostsList']);
    const exantCostsData = dataExtant.getIn(['exAnteCostList']);

    let query = {
        productKID: '',
        contract: '',
        marketingReport: '',
        infoCcv: '',
        sendOrder: 'No',
        idOrder: orderId,
        market: marketName,
        termValidity: validityDate.format(DATE_FORMAT_API),
        holders: owner,
        leafletSusbcription: trim(immOperation.get('linkTriptico')),
        cnmvDocumentation: trim(immOperation.get('linkCNMV')),
        ccv: idContract, // franco.montenegro To confirm
        ccc: trim(get(buyRightsResponse, 'cuentaAsociada.codbban')), // franco.montenegro To confirm
        idContract: idContract, // franco.montenegro To confirm
        email: trim(PersonalDataHelper.getEmail(immProfile)),
        clientAlias: trim(immProfile.get('nombreCliente')),
        isin: trim(immOperation.get('isin')),
        isinDescribe: trim(immOperation.get('descriptionValue')),
        channel: CONNECTION_CHANNEL_INTERNET,
        orderType: BROKER_ORDER_TYPE_BUY,
        productType: BROKER_CORPORATE_OPERATIONS_RIGHTS_PRODUCT_TYPE,
        sharesNumber,
        sharesAmount,
        orderCondition: getOrderConditionDescription(orderTypeOption),
        conditionPrize : orderTypeValue ? `${orderTypeValue}` : '',
        currency: currency,
        mifidWarningText: generateMiFIDDisclaimers(getState().brokerConvenienceMiFIDTest),
        dateTime: momentEmittedDateTime.format(DATE_FORMAT_WITH_DASHES_DATETIME)
    };

    if (showExantCostETFBuy) {
        const copyQuery = query;
        query = {
            ...copyQuery,
            exAnteCostsList: exantCostsData
        }
    }

    const url = apiURLBuilder.getURL('communication');

    APIPost(dispatch, url, { query }).catch(() => dispatch(errorActions.show('', true, history)));
}

function confirmOrder(dataToken, history) {
    return function(dispatch, getState) {
        const data = getConfirmationRequestObject(dataToken, getState);

        dispatch(confirmRightsBuyRequest());
        return APIPost(dispatch, apiURLBuilder.getURL('brokerRightsBuyConfirmation'), data).then(
            function(response) {
                dispatch(confirmRightsBuySuccess(response));
                dispatch(storeDocument(response, history));
            },
            function(response) {
                dispatch(confirmRightsBuyFailure(response));
                const errorKey = get(response, ['body', 'error'], '');
                dispatch(errorActions.show('brokerAPIErrors-' + errorKey, true, history));
            }
        );
    };
}

const validateSignaturePattern = (history) => (dispatch, getState) => {
    const callback = (dispatch, getState, dataToken) => {
        dispatch(confirmOrder(dataToken, history));
    };
    signaturePatternActions.validateAndExecute(dispatch, getState, callback);
};

function validateWithAPIRequest(step) {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_VALIDATE_REQUEST,
        payload: {
            step
        }
    };
}

function validateWithAPISuccess(step, response) {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_VALIDATE_SUCCESS,
        payload: {
            data: response,
            step
        }
    };
}

function validateWithAPIFailure(step, response) {
    return {
        type: actionTypes.BROKER_RIGHTS_BUY_VALIDATE_FAILURE,
        payload: {
            data: response,
            step
        }
    };
}

const getValidationRequestObject = getState => {
    const immBrokerRightsBuy = getState().brokerRightsBuy;
    const immCorporatesOperations = getState().brokerCorporatesOperations;
    const selectedCorporateOperationIndex = immBrokerRightsBuy.get(
        'selectedCorporateOperationIndex'
    );
    const selectedCorporateOperation = immCorporatesOperations.getIn([
        'selectedContract',
        'operations',
        selectedCorporateOperationIndex
    ]);
    const selectedContract = immCorporatesOperations.get('selectedContract');
    const validityDate = immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'validityDate']);
    const orderTypeOption = immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'orderTypeOption']);

    const requestObject = {
        query: {
            codigoEmision: selectedCorporateOperation.get('codigoEmision'),
            codigoValor: selectedCorporateOperation.get('codigoValorInterno'),
            fechaSituacion: DateHelper.formatDate(new Date(), DATE_FORMAT_API),
            moduloDepositanteDigito: selectedContract.getIn(['cuenta', 'digitodecontrol']),
            plazoValidez: DateHelper.formatDate(validityDate, DATE_FORMAT_API),
            tipoOrden: getOrderTypeCode(orderTypeOption),
            titulos: immBrokerRightsBuy.getIn(['steps', FIRST_STEP, 'rightsQuantity']),
            producto: selectedContract.getIn(['contract', 'product']),
            numeroContrato: selectedContract.getIn(['contract', 'number'])
        }
    };

    return requestObject;
};

const validateWithAPI = (step, history) => (dispatch, getState) => {
    const valid = getState().brokerRightsBuy.getIn(['steps', FIRST_STEP, 'valid']);

    if (valid) {
        const data = getValidationRequestObject(getState);

        dispatch(validateWithAPIRequest(step));
        return APIGet(dispatch, apiURLBuilder.getURL('brokerRightsBuyValidation'), data).then(
            function(response) {
                dispatch(validateWithAPISuccess(step, response));
            },
            function(response) {
                dispatch(validateWithAPIFailure(step, response));
                const errorKey = get(response, ['body', 'error'], '');
                dispatch(errorActions.show('brokerAPIErrors-' + errorKey, true, history));
            }
        );
    }
};

const validateStep = (step, history) => (dispatch, getState) => {
    const catalogData = getState().brokerProductFile.get('catalogData');
    dispatch(validateUIStep(step, catalogData));
    switch (step) {
        case WIZARD_FIRST_STEP:
            dispatch(validateWithAPI(step, history));
            break;
        case WIZARD_SECOND_STEP:
            const valid = getState().brokerRightsBuy.get('steps').get(SECOND_STEP).get('valid');
            if (valid) {
                dispatch(validateSignaturePattern(history));
            }
            break;
    }
};

const setWillCancel = willCancel => ({
    type: actionTypes.BROKER_RIGHTS_BUY_WILL_CANCEL,
    payload: {
        willCancel
    }
});

const toggleDisclaimerCheckbox = evt => ({
    type: actionTypes.BROKER_RIGHTS_BUY_DISCLAIMER_CLAUSE_CHANGED,
    payload: {
        checked: evt.target.checked
    }
});

const finish = () => () => {};

const continueOperationFromMifid = (operationDataBeforeMifid, history) => (dispatch) => {
    dispatch({
        type: actionTypes.BROKER_RIGHTS_BUY_CONTINUE_FROM_MIFID,
        payload: { operationData: operationDataBeforeMifid }
    });

    history.push(BROKER_CORPORATE_OPERATIONS_BUY_RIGHTS_URL);
};

const setDocumentsRead = () => (dispatch) => dispatch({
    type: actionTypes.BROKER_RIGHTS_BUY_DOCUMENTS_READ
});

function exAntCostRequest() {
    return {
        type: actionTypes.BROKER_RIGHTS_EXTANTCOST_FECHING,
    };
}

function exAntCostSuccess(data) {
    return {
        type: actionTypes.BROKER_RIGHTS_EXTANTCOST_SUCESS,
        payload: data
    };
}

function exAntCostError(error) {
    return {
        type: actionTypes.BROKER_RIGHTS_EXTANTCOST_ERROR,
        payload: error
    };
}

const getExAntCostBuyRight = (history) => (dispatch, getState) => {
    const immStepOne = getState()['brokerRightsBuy'].getIn(['steps', FIRST_STEP])
    const immOrderType = immStepOne.getIn(['orderTypeOption']);
    let orderType = '';
    let limitedPrice = null;
    const sharesAmount = null;
    const sharesNumber = immStepOne.getIn(['rightsQuantity']);

    switch (immOrderType) {
        case 'toMarket':
            orderType = 'MARKET';
            break;
        case 'forTheBest':
            orderType = 'BEST';
            break;
        case 'limited':
            orderType = 'LIMITED';
            limitedPrice = {
                amount: immStepOne.getIn(['orderTypeValue']),
                currency: getState().brokerProductFile.get('currency'),
                descriptionKey: null,
                percentage: null,
                tooltipKey: null,
                type: null
            }
            break;
    }
    const selectedCorporateOperationIndex = getState().brokerRightsBuy.get('selectedCorporateOperationIndex');
    const immSelectedContract = getState().brokerCorporatesOperations.getIn(['selectedContract', 'contract'])
    const isinCode = getState().brokerCorporatesOperations.getIn(['selectedContract', 'operations', selectedCorporateOperationIndex, 'isin'], false);
    const marketCode = getState().brokerCorporatesOperations.getIn(['selectedContract', 'operations', selectedCorporateOperationIndex, 'iso'], false);
    const data = {
        channel: __CONTACT_CENTER__ ? getCCOfficeChannel() : API_CHANNEL,
        contract: {
          number: immSelectedContract.getIn(['number']),
          product: immSelectedContract.getIn(['product']),
        },
        instrumentType: 'RIGHT',
        isin: isinCode,
        limitedPrice,
        market: marketCode,
        operationType: 'C',
        orderType,
        sharesAmount,
        sharesNumber
    }

    dispatch(exAntCostRequest());
    return APIPost(dispatch, apiURLBuilder.getURL('brokerGetExAnteCost'), { query: data }).then(
        function (response) {
            dispatch(exAntCostSuccess(response));
        },
        function (error) {
            const errorKey = get(error, ['body', 'error'], '') || error.error;
            dispatch(exAntCostError(error));
            dispatch(errorActions.show('brokerAPIErrors-' + errorKey, true, history));
        }
    );
}

module.exports = {
    continueOperationFromMifid,
    finish,
    limitedValueChanged,
    mifidDisclaimersResponse,
    orderTypeChanged,
    reset,
    rightsQuantityChanged,
    selectedCorporateOperationIndexChanged,
    setVisibleStep,
    setWillCancel,
    toggleDisclaimerCheckbox,
    validateStep,
    validityDateChanged,
    setDocumentsRead,
    getExAntCostBuyRight
};
